import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

export type QueryParams = Record<string, string | string[] | undefined>;
export type QueryUpdate = Record<string, string | string[] | null | undefined>;

type UseQueryParams = {
  query: QueryParams;
  updateQuery: (updates: QueryUpdate) => void;
  resetQuery: () => void;
  getQueryParam: (key: string) => string | undefined;
  getAllQueryParams: (key: string) => string[];
  hasQueryParam: (key: string) => boolean;
  getMergedSearchParams: (updates: QueryUpdate) => string;
};

const mergeParams = (base: URLSearchParams, updates: QueryUpdate) => {
  const next = new URLSearchParams(base);

  for (const [key, value] of Object.entries(updates)) {
    if (value === null) {
      next.delete(key);
      continue;
    }

    if (Array.isArray(value)) {
      next.delete(key);
      for (const v of value) next.append(key, v);
      continue;
    }

    if (value !== undefined) {
      next.set(key, String(value));
    }
  }

  return next;
};

export const useQueryParams = (): UseQueryParams => {
  const [params, setParams] = useSearchParams();

  const query = useMemo<QueryParams>(() => {
    const result: QueryParams = {};
    params.forEach((value, key) => {
      const existing = result[key];
      if (existing) {
        if (typeof existing === "string") result[key] = [existing, value];
        else (existing as string[]).push(value);
      } else {
        result[key] = value;
      }
    });
    return result;
  }, [params]);

  const updateQuery = useCallback(
    (updates: QueryUpdate) => {
      setParams(mergeParams(params, updates), { replace: true });
    },
    [params, setParams],
  );

  const getMergedSearchParams = useCallback(
    (updates: QueryUpdate) => mergeParams(params, updates).toString(),
    [params],
  );

  const resetQuery = useCallback(() => {
    setParams({}, { replace: true });
  }, [setParams]);

  const getQueryParam = useCallback(
    (key: string) => params.get(key) ?? undefined,
    [params],
  );

  const getAllQueryParams = useCallback(
    (key: string) => params.getAll(key),
    [params],
  );

  const hasQueryParam = useCallback((key: string) => params.has(key), [params]);

  return {
    query,
    updateQuery,
    resetQuery,
    getQueryParam,
    getAllQueryParams,
    hasQueryParam,
    getMergedSearchParams,
  };
};
